/*
  tcl-filter-bar — client-side search + chip filtering over SERVER-RENDERED items.

  Shared by tcl-treatment-index (/pages/treatments) and main-collection-product-grid
  (/collections/*). The blog index carries the same bar in SERVER mode (real links,
  a real GET form) and never loads this file.

  THE CONTRACT (all data attributes, no classes, so a host can restyle freely):

    [data-tfb-root]                 wraps the bar AND the items; gets `is-enhanced`
      [data-tfb-bar]                the sticky <search> landmark
        [data-tfb-form]             one <form> that never submits
        [data-tfb-q]                the search input
        [data-tfb-qclear]           clears + refocuses the input; hidden when empty
        [data-tfb-sets]             container of chip sets
          [data-tfb-set="S"]        one set, e.g. "concern"; carries a label
            [data-tfb-chip="key"]   a toggle; `aria-pressed` is the state
        [data-tfb-count]            `data-all` / `data-fmt` templates ([n], [m])
        [data-tfb-reset]            clears everything; hidden when nothing is set
        [data-tfb-all]              optional "search everything" link; `data-href`
                                    is the base, the query is appended
      [data-tfb-group="key"]        optional grouping wrapper (a concern heading and
        [data-tfb-group-title]        its cards); hidden when it holds no visible item.
      [data-tfb-item]               one filterable thing
        data-search                 normalised haystack, every term must appear
        data-tfb-<S>="k1 k2"        the item's keys in set S (space-separated)

  THE MODEL:
      visible = (for every set S with ≥1 selected chip: item ∩ selected(S) ≠ ∅)
            AND every search term appears in data-search
  Chips within a set are OR; sets are AND — "Hair Loss OR Dandruff, AND Shampoo".
  No chip selected in a set means that set does not constrain.

  CHIPS ARE SERVER-RENDERED OR BUILT FROM GROUPS, never from a second list. When a
  set is empty at enhance time and the root has `[data-tfb-group][data-set=S]`
  elements, one chip per group is created from the group's own title — so the
  index's concern chips cannot drift from the concern headings they filter. The
  collection renders its chips in Liquid from `collection.all_tags`, the same
  source its items are tagged from.

  EVERY ITEM IS ALREADY IN THE HTML. This script only ever sets `hidden`; it
  never builds an item. These pages replace URLs that already rank and Googlebot
  is served from a separate long-TTL pool on this store, so a client-rendered
  list would be a real risk. With JS off the bar is display:none and the whole
  list is readable.

  DELEGATED FROM `document` like tcl-price-list, so a Theme Editor section reload
  needs no per-section teardown, and two roots on one page work with every
  lookup scoped to the nearest root.

  RE-APPLIES WHEN THE HOST RE-RENDERS. Dawn's facets.js swaps the product grid's
  innerHTML on every storefront-filter or sort change; the bar lives OUTSIDE that
  container so it survives, and a MutationObserver on `[data-tfb-rerender]`
  re-applies the current query and chips to the fresh items.

  DEEP LINKS. A host may mark the root `data-tfb-anchors`; then, once enhanced,
  the root publishes `--tl-anchor-offset` (the bar's measured bottom edge plus a
  gap) so a `#tl-concern-…` jump lands BELOW the bar, and re-runs the initial
  fragment scroll once — the browser performed it before this script existed,
  when the bar was still display:none.
*/
(function () {
  if (window.tclFilterBar) return;
  window.tclFilterBar = true;

  var ROOT = '[data-tfb-root]';

  function $all(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function setKeys(root) {
    var keys = [];
    $all(root, '[data-tfb-set]').forEach(function (s) {
      var k = s.getAttribute('data-tfb-set');
      if (k && keys.indexOf(k) === -1) keys.push(k);
    });
    return keys;
  }

  function selected(root, set) {
    var on = [];
    $all(root, '[data-tfb-set="' + set + '"] [data-tfb-chip]').forEach(function (b) {
      if (b.getAttribute('aria-pressed') === 'true' && !b.hidden) on.push(b.getAttribute('data-tfb-chip'));
    });
    return on;
  }

  /* One chip per group, from the group's own title, when the set shipped empty. */
  function buildChips(root) {
    $all(root, '[data-tfb-set]').forEach(function (setEl) {
      if (setEl.querySelector('[data-tfb-chip]')) return;
      var set = setEl.getAttribute('data-tfb-set');
      $all(root, '[data-tfb-group][data-set="' + set + '"]').forEach(function (g) {
        var title = g.querySelector('[data-tfb-group-title]');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'tl-fbar__chip';
        b.setAttribute('data-tfb-chip', g.getAttribute('data-tfb-group'));
        b.setAttribute('aria-pressed', 'false');
        b.textContent = (title ? title.textContent : g.getAttribute('data-tfb-group')).trim();
        setEl.appendChild(b);
      });
    });
    pruneChips(root);
  }

  /* A chip that matches EVERY item, or none, cannot narrow anything — it reads
     as a filter and does nothing. A housekeeping tag carried by all 27 products
     is the live example. Counted on the rendered items, so the rule is the same
     whatever the host's source; and a set left with fewer than two live chips is
     hidden whole, label included. */
  function pruneChips(root) {
    var items = $all(root, '[data-tfb-item]');
    $all(root, '[data-tfb-set]').forEach(function (setEl) {
      var set = setEl.getAttribute('data-tfb-set');
      var live = 0;
      $all(setEl, '[data-tfb-chip]').forEach(function (chip) {
        var key = chip.getAttribute('data-tfb-chip');
        var n = items.filter(function (it) {
          return (' ' + (it.getAttribute('data-tfb-' + set) || '') + ' ').indexOf(' ' + key + ' ') !== -1;
        }).length;
        var useless = n === 0 || (n === items.length && items.length > 0);
        chip.hidden = useless;
        if (useless) chip.setAttribute('aria-pressed', 'false');
        if (!useless) live++;
      });
      setEl.hidden = live < 2;
    });
  }

  function apply(root) {
    var sets = setKeys(root);
    var sel = {};
    var anyChip = false;
    sets.forEach(function (s) {
      sel[s] = selected(root, s);
      if (sel[s].length) anyChip = true;
    });

    var qEl = root.querySelector('[data-tfb-q]');
    var q = qEl ? norm(qEl.value) : '';
    var terms = q ? q.split(' ') : [];

    var shown = 0;
    var total = 0;

    $all(root, '[data-tfb-item]').forEach(function (item) {
      total++;
      var on = true;
      for (var i = 0; i < sets.length && on; i++) {
        var want = sel[sets[i]];
        if (!want.length) continue;
        var have = (item.getAttribute('data-tfb-' + sets[i]) || '').split(/\s+/);
        var hit = false;
        for (var j = 0; j < have.length; j++) {
          if (want.indexOf(have[j]) !== -1) { hit = true; break; }
        }
        on = hit;
      }
      if (on && terms.length) {
        var hay = item.getAttribute('data-search') || '';
        /* Every term must appear — "scalp shampoo" should not match every item
           containing "scalp". */
        for (var k = 0; k < terms.length; k++) {
          if (hay.indexOf(terms[k]) === -1) { on = false; break; }
        }
      }
      item.hidden = !on;
      if (on) shown++;
    });

    /* A heading over nothing is the same defect the Liquid gates against
       server-side: a group follows its items. */
    $all(root, '[data-tfb-group]').forEach(function (g) {
      g.hidden = !$all(g, '[data-tfb-item]').some(function (it) { return !it.hidden; });
    });

    var none = root.querySelector('[data-tfb-none]');
    if (none) none.hidden = shown !== 0;

    var filtering = anyChip || terms.length > 0;

    var count = root.querySelector('[data-tfb-count]');
    if (count) {
      /* Square-bracket tokens, not curly braces: a curly placeholder in a
         merchant-typed setting would be parsed as Liquid output. */
      if (!filtering) {
        count.textContent = (count.getAttribute('data-all') || '[m] items').replace('[m]', String(total));
      } else {
        count.textContent = (count.getAttribute('data-fmt') || 'Showing [n] of [m]')
          .replace('[n]', String(shown))
          .replace('[m]', String(total));
      }
    }

    var reset = root.querySelector('[data-tfb-reset]');
    if (reset) reset.hidden = !filtering;

    var qclear = root.querySelector('[data-tfb-qclear]');
    if (qclear) qclear.hidden = !(qEl && qEl.value);

    /* "Search all products" — only meaningful with a query, and only rendered by
       hosts whose list is paginated (the client filter sees one page). */
    var all = root.querySelector('[data-tfb-all]');
    if (all) {
      var raw = qEl ? qEl.value.trim() : '';
      all.hidden = !raw;
      if (raw) all.href = (all.getAttribute('data-href') || '/search?q=') + encodeURIComponent(raw);
    }
  }

  function reset(root) {
    $all(root, '[data-tfb-chip]').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
    var q = root.querySelector('[data-tfb-q]');
    if (q) q.value = '';
    apply(root);
  }

  /* Publishes the bar's measured bottom edge as the anchor offset for the root's
     own [data-tcl-anchor] descendants (custom properties inherit, so setting it
     on the root scopes it — the page nav's global publish is untouched). Measured,
     never a constant: the bar wraps to a different number of rows at every
     breakpoint, and --header-height itself changes with the announcement bar. */
  function publishAnchorOffset(root) {
    if (!root.hasAttribute('data-tfb-anchors')) return;
    var bar = root.querySelector('[data-tfb-bar]');
    if (!bar) return;
    /* The bar's HEIGHT is measured; the header it parks under is added the same
       way tcl-base's own fallback adds it, so the two cannot disagree about
       what the header measures on this viewport. */
    var write = function () {
      var h = Math.round(bar.getBoundingClientRect().height);
      root.style.setProperty(
        '--tl-anchor-offset',
        'calc(var(--header-height, 7rem) + ' + h + 'px + 1.6rem)'
      );
    };
    write();
    if (!bar.dataset.tfbObserved) {
      bar.dataset.tfbObserved = '1';
      if (window.ResizeObserver) new ResizeObserver(write).observe(bar);
      window.addEventListener('resize', write);
    }
    /* The browser scrolled to the fragment before this ran, while the bar was
       still display:none; do it once more now the bar has a height. */
    if (!root.dataset.tfbJumped && location.hash) {
      root.dataset.tfbJumped = '1';
      var id = decodeURIComponent(location.hash.slice(1));
      var target = id && root.querySelector('[id="' + id.replace(/"/g, '') + '"]');
      if (target) requestAnimationFrame(function () { target.scrollIntoView(); });
    }
  }

  function observeRerender(root) {
    var host = root.querySelector('[data-tfb-rerender]');
    if (!host || host.dataset.tfbObserved || !window.MutationObserver) return;
    host.dataset.tfbObserved = '1';
    new MutationObserver(function () { apply(root); }).observe(host, { childList: true, subtree: true });
  }

  function enhance() {
    $all(document, ROOT).forEach(function (root) {
      buildChips(root);
      root.classList.add('is-enhanced');
      apply(root);
      publishAnchorOffset(root);
      observeRerender(root);
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var chip = e.target.closest('[data-tfb-chip]');
    if (chip && chip.tagName === 'BUTTON') {
      chip.setAttribute('aria-pressed', chip.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      apply(chip.closest(ROOT));
      return;
    }
    var r = e.target.closest('[data-tfb-reset]');
    if (r && r.tagName === 'BUTTON') { reset(r.closest(ROOT)); return; }
    var qc = e.target.closest('[data-tfb-qclear]');
    if (qc) {
      var root = qc.closest(ROOT);
      var q = root.querySelector('[data-tfb-q]');
      if (q) { q.value = ''; q.focus(); }
      apply(root);
    }
  });

  document.addEventListener('input', function (e) {
    if (e.target.matches && e.target.matches('[data-tfb-q]')) {
      var root = e.target.closest(ROOT);
      if (root) apply(root);
    }
  });

  /* The form exists for the search landmark and Enter-to-search semantics; in
     client mode it must never navigate. A server-mode form has no root. */
  document.addEventListener('submit', function (e) {
    if (e.target.matches && e.target.matches('[data-tfb-form]') && e.target.closest(ROOT)) e.preventDefault();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance);
  } else {
    enhance();
  }
  document.addEventListener('shopify:section:load', enhance);
})();
